
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { Link } from "react-router";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { useCart } from "@/lib/cart";
import { useFeatures } from "@/lib/features";
import CartDrawer from "@/components/shop/CartDrawer";

export default function Navbar({ dark = false, sticky = false }: { dark?: boolean; sticky?: boolean }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const theme = usePalette();
  const { totalItems } = useCart();
  const { features } = useFeatures();
  const background = dark ? "#101116" : colors.cream;
  const foreground = dark ? "#eee7dc" : colors.navy;
  const border = dark ? "#bcb0cc26" : `${colors.tan}15`;

  // Shop + Guides links/cart only appear when their feature flag is on.
  const navLinks = [
    { label: "Home", href: "/" },
    { label: "About", href: "/about" },
    { label: "Videos", href: "/videos" },
    ...(features.guides ? [{ label: "Guides", href: "/guides" }] : []),
    { label: "My Favorites", href: "/favorites" },
    ...(features.shop ? [{ label: "Shop", href: "/shop" }] : []),
    { label: "Retreats", href: "/retreats" },
  ];

  return (
    <nav
      className={`${sticky ? "sticky" : "fixed"} top-0 left-0 right-0 z-50 backdrop-blur-md border-b`}
      style={{
        backgroundColor: `${background}ee`,
        borderColor: border,
      }}
    >
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center" aria-label="A Few Loose Threads — home">
          <img
            src="/logos/text-logo-purple-long.png"
            alt="A Few Loose Threads"
            className="h-7 w-auto"
            width={2000}
            height={183}
            style={dark ? { filter: "brightness(0) invert(1)", opacity: 0.85 } : undefined}
          />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className="text-sm tracking-[0.05em] hover:opacity-70 transition-opacity duration-200 cursor-pointer"
              style={{
                fontFamily: fonts.body,
                fontWeight: 600,
                color: foreground,
              }}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/#newsletter"
            className="px-5 py-2 rounded-full text-sm text-white cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105"
            style={{
              fontFamily: fonts.button,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase" as const,
              fontSize: "0.7rem",
              backgroundColor: dark ? "#e4be79" : theme.accent,
              color: dark ? "#1c1710" : undefined,
            }}
          >
            Stay in the Loop
          </Link>
          {features.shop && (
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 cursor-pointer hover:opacity-70 transition-opacity"
              style={{ color: foreground }}
              aria-label="Open cart"
            >
              <ShoppingBag size={22} />
              {totalItems > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] text-white"
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    backgroundColor: colors.berryPink,
                  }}
                >
                  {totalItems > 9 ? "9+" : totalItems}
                </span>
              )}
            </button>
          )}
        </div>

        <div className="md:hidden flex items-center gap-1">
          {features.shop && (
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 cursor-pointer"
              style={{ color: foreground }}
              aria-label="Open cart"
            >
              <ShoppingBag size={22} />
              {totalItems > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] text-white"
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    backgroundColor: colors.berryPink,
                  }}
                >
                  {totalItems > 9 ? "9+" : totalItems}
                </span>
              )}
            </button>
          )}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 cursor-pointer"
            style={{ color: foreground }}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden overflow-hidden border-t"
            style={{
              backgroundColor: background,
              borderColor: border,
            }}
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-sm tracking-[0.05em] py-2 cursor-pointer"
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 600,
                    color: foreground,
                  }}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/#newsletter"
                onClick={() => setMobileOpen(false)}
                className="inline-flex justify-center px-5 py-2.5 rounded-full text-sm text-white cursor-pointer mt-2"
                style={{
                  fontFamily: fonts.button,
                  fontWeight: 700,
                  backgroundColor: dark ? "#e4be79" : theme.accent,
                  color: dark ? "#1c1710" : undefined,
                }}
              >
                Stay in the Loop
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {features.shop && <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />}
    </nav>
  );
}
